import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { InputLabel } from '@components/Common';
import { ExternalData } from '@api/queries/inventory';
import { BookkeepingFormData } from '../BookkeepingEntryForm';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 25px auto 10px auto;
`;

const TableContainer = styled.div`
  margin: 3px;
  border: 1px solid hsl(0, 0%, 80%);
  border-radius: 4px;
  padding: 10px;
  min-width: 200px;

  .data {
    display: flex;
    flex-direction: row;

    &:nth-child(even) {
      background: #eee;
    }

    .dt {
      width: 140px;
      margin: 0;
    }
    .dd {
      flex: 1;
      margin: 0;

      &:before {
        content: '= ';
      }
    }
  }
`;

interface DataTableProps {
  heading: string;
  data: ExternalData[];
}

const DataTable: React.FC<DataTableProps> = ({ heading, data }) => {
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <TableContainer>
      <InputLabel>{heading}</InputLabel>
      {data.map((d) => (
        <div className="data" key={d.key}>
          <div className="dt">{d.key.toLocaleLowerCase()}</div>
          <div className="dd">{d.value}</div>
        </div>
      ))}
    </TableContainer>
  );
};

const ExternalDataSection: React.FC = () => {
  const { t } = useTranslation();
  const { watch } = useFormContext<BookkeepingFormData>();

  const externalData = watch('externalData');
  const flowMeterData = watch('flowMeterData');
  const hydrogenPeroxideFlowMeterData = watch('hydrogenPeroxideFlowMeterData');

  // Don't render anything if there's no external data
  const hasExternalData = externalData && externalData.length > 0;
  const hasFlowMeterData = flowMeterData && flowMeterData.length > 0;
  const hasH2O2FlowMeterData = hydrogenPeroxideFlowMeterData && hydrogenPeroxideFlowMeterData.length > 0;

  if (!hasExternalData && !hasFlowMeterData && !hasH2O2FlowMeterData) {
    return null;
  }

  return (
    <Container>
      {hasExternalData && (
        <DataTable heading={t('entry.truckscale')} data={externalData} />
      )}
      {hasFlowMeterData && (
        <DataTable heading={t('entry.flow_meter_data')} data={flowMeterData} />
      )}
      {hasH2O2FlowMeterData && (
        <DataTable heading={t('entry.flow_meter_data_h2o2')} data={hydrogenPeroxideFlowMeterData} />
      )}
    </Container>
  );
};

export default ExternalDataSection;
